import { useState } from "react";
import type { ReactNode } from "react";
import { useOpenPlayerProfile } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/useOpenPlayerProfile";
import type { InningScorecard } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { ballChip, ballLabel, colorFor, fmtViews, inningOrdinal, initials, isLiveInning, isWicketBall, scoreText, softFor, teamByName } from "./helpers";
import type { Ball, MatchInfo, TeamRef } from "./helpers";


export const Crest = ({ team, size }: { team: TeamRef; size?: number }) => {
  const [bad, setBad] = useState(false);
  const style = size ? { width: size, height: size, fontSize: size < 30 ? 8 : 12, boxShadow: "none" } : undefined;
  return (
    <span className="crest" style={{ background: colorFor(team.name), ...style }} aria-hidden="true">
      {team.image && !bad ? <img src={team.image} alt="" onError={() => setBad(true)} /> : team.code}
    </span>
  );
};

export const PlayerLink = ({ id, name }: { id?: string | null; name: string }) => {
  const open = useOpenPlayerProfile();
  if (!id?.trim()) return <>{name}</>;
  return (
    <button type="button" className="plink" onClick={() => open(id)}>
      {name}
    </button>
  );
};

export const Avatar = ({ name, team, src }: { name: string; team?: string | null; src?: string }) => {
  const [bad, setBad] = useState(false);
  return (
    <span className="av" style={{ background: softFor(team), color: colorFor(team) }} aria-hidden="true">
      {initials(name)}
      {src && !bad ? <img src={src} alt="" onError={() => setBad(true)} /> : null}
    </span>
  );
};

export const Chip = ({ ball }: { ball: Ball }) => {
  const c = ballChip(ball);
  return <span className={`bl ${c.cls}`}>{c.text}</span>;
};

export const Empty = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>;

type SegProps = { innings: InningScorecard[]; match?: MatchInfo; cur: number; onChange: (i: number) => void };
export const InningSeg = ({ innings, match, cur, onChange }: SegProps) => (
  <div className="iseg">
    {innings.map((inn, i) => (
      <button key={inn.inningId ?? i} type="button" aria-pressed={cur === i} onClick={() => onChange(i)}>
        {inningOrdinal(i)} · <Crest team={teamByName(match, inn.battingTeamName)} size={32} />
        {teamByName(match, inn.battingTeamName).name || inn.battingTeamName}
        <span className="n">{scoreText(inn)}</span>
        {isLiveInning(inn) && match?.matchStatus === "IN_PROGRESS" ? <span className="ldot" /> : null}
      </button>
    ))}
  </div>
);

export const BallRow = ({ ball, over }: { ball: Ball; over?: number }) => {
  const cls = isWicketBall(ball) ? "w" : ball.runs_scored === 6 ? "s" : ball.runs_scored === 4 ? "f" : "";
  return (
    <li className={cls}>
      <span className="lab">{ballLabel(ball, over)}</span>
      <Chip ball={ball} />
      <span className="tx">{ball.commentary?.trim() || (isWicketBall(ball) ? "Wicket" : `${ball.runs_scored ?? 0} run${ball.runs_scored === 1 ? "" : "s"}`)}</span>
    </li>
  );
};

/** Eye icon + compact count for the hero meta line. Renders nothing until the backend has a number. */
export const ViewsBadge = ({ count }: { count?: number | null }) =>
  typeof count === "number" && count >= 0 ? (
    <>
      <i />
      <span className="vw" title={`${count.toLocaleString()} views`} aria-label={`${count.toLocaleString()} views`}>
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <b>{fmtViews(count)}</b>views
      </span>
    </>
  ) : null;
